/**
 * OverviewToolbarComponent — reusable two-row toolbar for entity overview pages.
 *
 * Ported 1:1 from license-portal-ui's du-overview-toolbar.
 */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, distinctUntilChanged, Subject} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {SelectItem} from 'primeng/api';
import {Toolbar} from 'primeng/toolbar';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {BadgeModule} from 'primeng/badge';
import {SelectButton} from 'primeng/selectbutton';
import {Select} from 'primeng/select';
import {Chip} from 'primeng/chip';
import {TooltipModule} from 'primeng/tooltip';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ActiveFilter, ToolbarConfig} from './overview-toolbar.models';
import {ConfigQuickFilter} from '../models/config-overview.model';
import {LucideDynamicIcon, LucidePlus} from '@lucide/angular';

@Component({
    selector: 'du-overview-toolbar',
    imports: [
        FormsModule,
        Toolbar,
        Button,
        IconField,
        InputIcon,
        InputText,
        BadgeModule,
        SelectButton,
        Select,
        Chip,
        TooltipModule,
        TranslatePipe,
        LucideDynamicIcon,
    ],
    templateUrl: './overview-toolbar.component.html',
    styleUrl: './overview-toolbar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewToolbarComponent {
    config = input.required<ToolbarConfig>();
    view = input<'grid' | 'list'>('grid');

    primaryClick = output<void>();
    secondaryClick = output<void>();
    searchChange = output<string>();
    filterChange = output<{ key: string; value: unknown }>();
    filterRemoved = output<string>();
    filtersCleared = output<void>();
    sortChange = output<SelectItem>();
    viewChange = output<'grid' | 'list'>();

    protected readonly selectedSort = signal<SelectItem | null>(null);
    protected readonly searchValue = signal('');

    protected readonly lucidePlus = LucidePlus;

    protected readonly viewOptions: Array<{ value: 'grid' | 'list'; icon: string; tooltip: string }> =
        [
            {value: 'grid', icon: 'pi pi-th-large', tooltip: 'viewType.grid'},
            {value: 'list', icon: 'pi pi-list', tooltip: 'viewType.list'},
        ];

    private readonly translate = inject(TranslateService);
    protected readonly currentLang = signal(this.translate.currentLang || 'en');

    protected readonly badgeLabel = computed(() => {
        // Establish dependency on language changes
        const lang = this.currentLang();
        const {total, filteredCount} = this.config();

        const entryWord =
            total === 1
                ? this.translate.instant('entryMap.=1')
                : this.translate.instant('entryMap.other');

        if (filteredCount === undefined || filteredCount === total) {
            return `${total} ${entryWord}`;
        }

        if (lang === 'de') {
            return `${filteredCount} von ${total} angezeigt`;
        }
        return `Showing ${filteredCount} of ${total}`;
    });

    protected readonly searchPlaceholder = computed(() => {
        // Establish dependency on language changes
        const lang = this.currentLang();
        const cfg = this.config();
        if (cfg.searchPlaceholder) {
            return this.translate.instant(cfg.searchPlaceholder);
        }
        const translatedTitle = this.translate.instant(cfg.title);
        const translatedSearch = this.translate.instant('search');

        if (lang === 'de') {
            return `${translatedTitle} suchen...`;
        }
        return `${translatedSearch} ${translatedTitle.toLowerCase()}...`;
    });

    protected readonly activeFilters = computed<ActiveFilter[]>(
        () => this.config().activeFilters ?? [],
    );

    protected readonly sortOptions = computed<SelectItem[]>(() => this.config().sortOptions ?? []);

    protected readonly quickFilters = computed<ConfigQuickFilter[]>(
        () => this.config().quickFilters ?? [],
    );

    protected readonly filterValues = computed<Record<string, unknown>>(
        () => this.config().filterValues ?? {},
    );

    private readonly searchSubject = new Subject<string>();
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
            this.currentLang.set(event.lang);
        });

        this.searchSubject
            .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((val) => this.searchChange.emit(val));
    }

    protected onSearchInput(value: string): void {
        this.searchValue.set(value);
        this.searchSubject.next(value);
    }

    protected onQuickFilterChange(key: string, value: unknown): void {
        this.filterChange.emit({key, value});
    }

    protected onFilterRemove(key: string): void {
        this.filterRemoved.emit(key);
    }

    protected onClearAll(): void {
        this.filtersCleared.emit();
    }

    protected onSortChange(item: SelectItem): void {
        this.selectedSort.set(item);
        this.sortChange.emit(item);
    }

    protected onViewChange(view: 'grid' | 'list'): void {
        if (view) {
            this.viewChange.emit(view);
        }
    }
}
